import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AppTabName } from '../lib/types';

export type ViewStateKey =
  | 'dashboard'
  | 'pipeline'
  | 'missions'
  | 'affectation'
  | 'calendrier'
  | 'clients'
  | 'intervenants'
  | 'factures'
  | 'actions'
  | 'finance_ops'
  | 'data_quality'
  | 'integrations'
  | 'audit'
  | 'client360'
  | 'ai_insights'
  | 'privacy';

export interface ViewStateFilters {
  search?: string;
  status?: string;
  category?: string;
  flag?: string;
  scope?: string;
  monthKey?: string;
  demandDays?: string;
  entityId?: string;
  resourceId?: string;
  /** Calendar: open the event form on arrival, with this type (« Conge »). */
  createEvent?: string;
  owner?: string;
  severity?: string;
  connector?: string;
  entityType?: string;
  segment?: string;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  viewMode?: string;
}

export const EMPTY_VIEW_STATE: Readonly<ViewStateFilters> = Object.freeze({});

export type ThemeMode = 'light' | 'dark';

interface UiState {
  selectedYear: number;
  activeTab: AppTabName;
  compactMode: boolean;
  aiSidebarOpen: boolean;
  theme: ThemeMode;
  viewState: Partial<Record<ViewStateKey, ViewStateFilters>>;
  /** Notice id -> signature of the content that was dismissed. */
  dismissedNotices: Record<string, string>;
  setSelectedYear: (year: number) => void;
  setActiveTab: (tab: AppTabName) => void;
  setCompactMode: (compactMode: boolean) => void;
  setAiSidebarOpen: (open: boolean) => void;
  toggleAiSidebar: () => void;
  setTheme: (theme: ThemeMode) => void;
  patchViewState: (view: ViewStateKey, patch: Partial<ViewStateFilters>) => void;
  replaceViewState: (view: ViewStateKey, next: ViewStateFilters) => void;
  resetViewState: (view: ViewStateKey) => void;
  dismissNotice: (id: string, signature: string) => void;
}

function sanitizeViewState(value: Partial<ViewStateFilters> | undefined): ViewStateFilters {
  const entries = Object.entries(value || {}).filter(([, raw]) => {
    if (raw == null) return false;
    if (typeof raw !== 'string') return true;
    return raw.trim().length > 0;
  });
  return Object.fromEntries(entries) as ViewStateFilters;
}

const PERSISTED_VIEW_STATE_KEYS = new Set<keyof ViewStateFilters>([
  'status',
  'category',
  'flag',
  'scope',
  'monthKey',
  'severity',
  'connector',
  'entityType',
  'segment',
  'sortKey',
  'sortDirection',
]);

// Per-view keys that must never be persisted even though they are generally
// persistable. The calendar always opens on the current period, so a stale
// persisted month must not pull its anchor to another exercise on reload (T15).
const NON_PERSISTED_VIEW_KEYS: Partial<Record<ViewStateKey, ReadonlySet<keyof ViewStateFilters>>> = {
  calendrier: new Set<keyof ViewStateFilters>(['monthKey']),
  intervenants: new Set<keyof ViewStateFilters>(['monthKey', 'demandDays', 'flag']),
  integrations: new Set<keyof ViewStateFilters>(['entityType']),
};

export function sanitizePersistedViewState(value: unknown): Partial<Record<ViewStateKey, ViewStateFilters>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result: Partial<Record<ViewStateKey, ViewStateFilters>> = {};
  for (const [view, rawFilters] of Object.entries(value as Record<string, unknown>)) {
    if (!rawFilters || typeof rawFilters !== 'object' || Array.isArray(rawFilters)) continue;
    const blockedKeys = NON_PERSISTED_VIEW_KEYS[view as ViewStateKey];
    const safeFilters = Object.fromEntries(
      Object.entries(rawFilters as Record<string, unknown>).filter(([key, raw]) =>
        PERSISTED_VIEW_STATE_KEYS.has(key as keyof ViewStateFilters)
        && !blockedKeys?.has(key as keyof ViewStateFilters)
        && (typeof raw !== 'string' || raw.trim().length > 0),
      ),
    ) as ViewStateFilters;
    if (Object.keys(safeFilters).length > 0) result[view as ViewStateKey] = safeFilters;
  }
  return result;
}

function sameViewState(left: ViewStateFilters | undefined, right: ViewStateFilters | undefined): boolean {
  const leftEntries = Object.entries(left || {});
  const rightEntries = Object.entries(right || {});
  if (leftEntries.length !== rightEntries.length) return false;
  return leftEntries.every(([key, value]) => right?.[key as keyof ViewStateFilters] === value);
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      selectedYear: 2026,
      activeTab: 'dashboard',
      compactMode: false,
      aiSidebarOpen: false,
      theme: 'light' as ThemeMode,
      viewState: {},
      dismissedNotices: {},
      setSelectedYear: (selectedYear) =>
        set((state) => (state.selectedYear === selectedYear ? state : { selectedYear })),
      setActiveTab: (activeTab) =>
        set((state) => (state.activeTab === activeTab ? state : { activeTab })),
      setCompactMode: (compactMode) =>
        set((state) => (state.compactMode === compactMode ? state : { compactMode })),
      setAiSidebarOpen: (aiSidebarOpen) =>
        set((state) => (state.aiSidebarOpen === aiSidebarOpen ? state : { aiSidebarOpen })),
      toggleAiSidebar: () =>
        set((state) => ({ aiSidebarOpen: !state.aiSidebarOpen })),
      setTheme: (theme) =>
        set((state) => (state.theme === theme ? state : { theme })),
      patchViewState: (view, patch) =>
        set((state) => {
          const current = state.viewState[view] || EMPTY_VIEW_STATE;
          const nextViewState = sanitizeViewState({
            ...current,
            ...patch,
          });
          if (sameViewState(current, nextViewState)) return state;
          return {
            viewState: {
              ...state.viewState,
              [view]: nextViewState,
            },
          };
        }),
      replaceViewState: (view, next) =>
        set((state) => {
          const nextViewState = sanitizeViewState(next);
          const current = state.viewState[view] || EMPTY_VIEW_STATE;
          if (sameViewState(current, nextViewState)) return state;
          return {
            viewState: {
              ...state.viewState,
              [view]: nextViewState,
            },
          };
        }),
      resetViewState: (view) =>
        set((state) => {
          if (!state.viewState[view]) return state;
          const next = { ...state.viewState };
          delete next[view];
          return { viewState: next };
        }),
      // The signature is stored alongside the id so a notice whose content
      // changed comes back: dismissing "3 missions unplanned" must not hide a
      // later "7 missions unplanned".
      dismissNotice: (id, signature) =>
        set((state) => ({ dismissedNotices: { ...state.dismissedNotices, [id]: signature } })),
    }),
    {
      name: 'aircs-react-ui-v2',
      partialize: (state) => ({
        dismissedNotices: state.dismissedNotices,
        selectedYear: state.selectedYear,
        activeTab: state.activeTab,
        compactMode: state.compactMode,
        aiSidebarOpen: false,
        theme: state.theme,
        viewState: sanitizePersistedViewState(state.viewState),
      }),
      migrate: (persisted: unknown) => {
        const raw = (persisted && typeof persisted === 'object' ? persisted : {}) as Record<string, unknown>;
        return {
          selectedYear: typeof raw.selectedYear === 'number' ? raw.selectedYear : 2026,
          activeTab: typeof raw.activeTab === 'string' ? raw.activeTab : 'dashboard',
          compactMode: typeof raw.compactMode === 'boolean' ? raw.compactMode : false,
          aiSidebarOpen: false,
          theme: raw.theme === 'dark' ? 'dark' : 'light',
          viewState: sanitizePersistedViewState(raw.viewState),
          dismissedNotices: raw.dismissedNotices && typeof raw.dismissedNotices === 'object' && !Array.isArray(raw.dismissedNotices)
            ? Object.fromEntries(
                Object.entries(raw.dismissedNotices as Record<string, unknown>)
                  .filter(([, value]) => typeof value === 'string'),
              ) as Record<string, string>
            : {},
        };
      },
      version: 3,
    },
  ),
);
